import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'react-quill/dist/quill.snow.css';
import ReactQuill from 'react-quill';
import SuccessModal from '../ResumeBuilder/SuccessModal';
import DeleteConfirmModal from '../ResumeBuilder/DeleteConfirmModal';

const API_BASE_URL = 'https://www.testometer.in:8084/api';

const AdminMasterData = () => {
  const [activeTab, setActiveTab] = useState('skills');
  const [skills, setSkills] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal states
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  
  // Form states
  const [skillForm, setSkillForm] = useState({
    name: '',
    category: '',
    description: ''
  });
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    responsibilities: '',
    technologies: '',
    category: '',
    difficulty: '',
    client: '',
    domain: ''
  });

  /** --- Add state for search terms and pagination --- */
  const [skillSearchTerm, setSkillSearchTerm] = useState('');
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [skillPage, setSkillPage] = useState(1);
  const [projectPage, setProjectPage] = useState(1);
  const itemsPerPage = 10;

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(''); // 'skill' or 'project'
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    let timer;
    if (showSuccessModal) {
      timer = setTimeout(() => setShowSuccessModal(false), 3000);
    }
    return () => clearTimeout(timer);
  }, [showSuccessModal]);

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const [skillsResponse, projectsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/master-data/technical-skills`),
        axios.get(`${API_BASE_URL}/master-data/projects`)
      ]);
      setSkills(skillsResponse.data);
      setProjects(projectsResponse.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
      setError('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  const handleSkillSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSkill) {
        await axios.put(`${API_BASE_URL}/master-data/technical-skills/${editingSkill.id}`, skillForm);
      } else {
        await axios.post(`${API_BASE_URL}/master-data/technical-skills`, skillForm);
      }
      setShowSkillModal(false);
      setEditingSkill(null);
      setSkillForm({ name: '', category: '', description: '' });
      fetchMasterData();
      setShowSuccessModal(true);
      setSuccessMessage(editingSkill ? 'Skill updated successfully!' : 'Skill added successfully!');
    } catch (error) {
      console.error('Error saving skill:', error);
      setError('Failed to save skill');
    }
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await axios.put(`${API_BASE_URL}/master-data/projects/${editingProject.id}`, projectForm);
      } else {
        await axios.post(`${API_BASE_URL}/master-data/projects`, projectForm);
      }
      setShowProjectModal(false);
      setEditingProject(null);
      setProjectForm({ name: '', description: '', responsibilities: '', technologies: '', category: '', difficulty: '', client: '', domain: '' });
      fetchMasterData();
      setShowSuccessModal(true);
      setSuccessMessage(editingProject ? 'Project updated successfully!' : 'Project added successfully!');
    } catch (error) {
      console.error('Error saving project:', error);
      setError('Failed to save project');
    }
  };

  const handleEditSkill = (skill) => {
    setEditingSkill(skill);
    setSkillForm({
      name: skill.name,
      category: skill.category,
      description: skill.description
    });
    setShowSkillModal(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setProjectForm({
      name: project.name,
      description: project.description,
      responsibilities: project.responsibilities,
      technologies: project.technologies,
      category: project.category,
      difficulty: project.difficulty,
      client: project.client || '',
      domain: project.domain || ''
    });
    setShowProjectModal(true);
  };

  const handleDeleteSkill = async (skillId) => {
    try {
      await axios.delete(`${API_BASE_URL}/master-data/technical-skills/${skillId}`);
      fetchMasterData();
    } catch (error) {
      console.error('Error deleting skill:', error);
      setError('Failed to delete skill');
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await axios.delete(`${API_BASE_URL}/master-data/projects/${projectId}`);
      fetchMasterData();
    } catch (error) {
      console.error('Error deleting project:', error);
      setError('Failed to delete project');
    }
  };

  const renderSkillsTab = () => {
    // Filtered and paginated skills
    const filteredSkills = skills.filter(skill =>
      skill.name.toLowerCase().includes(skillSearchTerm.toLowerCase()) ||
      skill.category.toLowerCase().includes(skillSearchTerm.toLowerCase())
    );
    const totalSkillPages = Math.ceil(filteredSkills.length / itemsPerPage) || 1;
    const paginatedSkills = filteredSkills.slice((skillPage - 1) * itemsPerPage, skillPage * itemsPerPage);

    return (
      <div className="master-data-content">
        <div className="content-header">
          <h2>Technical Skills Management</h2>
          <button
            onClick={() => {
              setEditingSkill(null);
              setSkillForm({ name: '', category: '', description: '' });
              setShowSkillModal(true);
              console.log('Opening new skill modal, showSkillModal:', true);
            }}
            className="btn btn-primary"
          >
            + Add New Skill
          </button>
        </div>
        {/* Skill Search */}
        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            className="input-field"
            placeholder="Search skills by name or category..."
            value={skillSearchTerm}
            onChange={e => { setSkillSearchTerm(e.target.value); setSkillPage(1); }}
            style={{ maxWidth: 350 }}
          />
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSkills.map(skill => (
                <tr key={skill.id}>
                  <td><strong>{skill.name}</strong></td>
                  <td>{skill.category}</td>
                  <td>{skill.description}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEditSkill(skill)}
                        className="btn btn-small btn-primary"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => { setItemToDelete(skill.id); setDeleteType('skill'); setShowDeleteModal(true); }}
                        className="btn btn-small btn-danger"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination Controls */}
        <div className="pagination" style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
          <button className="pagination-btn" onClick={() => setSkillPage(p => Math.max(1, p - 1))} disabled={skillPage === 1}>Prev</button>
          {Array.from({ length: totalSkillPages }, (_, i) => (
            <button
              key={i + 1}
              className={`pagination-btn${skillPage === i + 1 ? ' active' : ''}`}
              onClick={() => setSkillPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button className="pagination-btn" onClick={() => setSkillPage(p => Math.min(totalSkillPages, p + 1))} disabled={skillPage === totalSkillPages}>Next</button>
        </div>
      </div>
    );
  };

  const renderProjectsTab = () => {
    // Filtered and paginated projects
    const filteredProjects = projects.filter(project =>
      project.name.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
      project.category.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
      (project.technologies && project.technologies.toLowerCase().includes(projectSearchTerm.toLowerCase()))
    );
    const totalProjectPages = Math.ceil(filteredProjects.length / itemsPerPage) || 1;
    const paginatedProjects = filteredProjects.slice((projectPage - 1) * itemsPerPage, projectPage * itemsPerPage);

    return (
      <div className="master-data-content">
        <div className="content-header">
          <h2>Projects Management</h2>
          <button
            onClick={() => {
              setEditingProject(null);
              setProjectForm({ name: '', description: '', responsibilities: '', technologies: '', category: '', difficulty: '', client: '', domain: '' });
              setShowProjectModal(true);
              console.log('Opening new project modal, showProjectModal:', true);
            }}
            className="btn btn-primary"
          >
            + Add New Project
          </button>
        </div>
        {/* Project Search */}
        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            className="input-field"
            placeholder="Search projects by name, category, or technology..."
            value={projectSearchTerm}
            onChange={e => { setProjectSearchTerm(e.target.value); setProjectPage(1); }}
            style={{ maxWidth: 350 }}
          />
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Difficulty</th>
                <th>Technologies</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProjects.map(project => (
                <tr key={project.id}>
                  <td><strong>{project.name}</strong></td>
                  <td>{project.category}</td>
                  <td>
                    <span className={`difficulty-badge ${project.difficulty.toLowerCase()}`}>
                      {project.difficulty}
                    </span>
                  </td>
                  <td>{project.technologies}</td>
                  <td>{project.description}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEditProject(project)}
                        className="btn btn-small btn-primary"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => { setItemToDelete(project.id); setDeleteType('project'); setShowDeleteModal(true); }}
                        className="btn btn-small btn-danger"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination Controls */}
        <div className="pagination" style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
          <button className="pagination-btn" onClick={() => setProjectPage(p => Math.max(1, p - 1))} disabled={projectPage === 1}>Prev</button>
          {Array.from({ length: totalProjectPages }, (_, i) => (
            <button
              key={i + 1}
              className={`pagination-btn${projectPage === i + 1 ? ' active' : ''}`}
              onClick={() => setProjectPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button className="pagination-btn" onClick={() => setProjectPage(p => Math.min(totalProjectPages, p + 1))} disabled={projectPage === totalProjectPages}>Next</button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="master-data">
        <div className="loading-overlay">
          <div className="loading-modal">
            <div className="spinner"></div>
            <span>Loading master data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <br/>
    <div className="master-data">
      {/* <div className="master-data-header">
        <h1>Master Data Management</h1>
        <p>Manage technical skills and projects that can be used in resumes</p>
      </div> */}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="tab-navigation">
        <button
          onClick={() => setActiveTab('skills')}
          className={`tab-btn ${activeTab === 'skills' ? 'active' : ''}`}
        >
          💻 Technical Skills ({skills.length})
        </button>
        <button
          onClick={() => setActiveTab('projects')}
          className={`tab-btn ${activeTab === 'projects' ? 'active' : ''}`}
        >
          🚀 Projects ({projects.length})
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'skills' ? renderSkillsTab() : renderProjectsTab()}
      </div>

      {/* Skill Modal */}
      {showSkillModal && (
        <div className="modal-overlay">
          <div className="modal-content large" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>{editingSkill ? 'Edit Skill' : 'Add New Skill'}</h3>
              <button
                onClick={() => {
                  setShowSkillModal(false);
                  console.log('Closing skill modal, showSkillModal:', false);
                }}
                className="modal-close"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSkillSubmit} className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Skill Name *</label>
                  <input
                    type="text"
                    value={skillForm.name}
                    onChange={(e) => setSkillForm({...skillForm, name: e.target.value})}
                    required
                    className="input-field"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select
                    value={skillForm.category}
                    onChange={(e) => setSkillForm({...skillForm, category: e.target.value})}
                    required
                    className="input-field"
                  >
                    <option value="">Select Category</option>
                     <option value="Programming Languages">Programming Languages</option>
                     <option value="Frameworks">Frameworks</option>
                     <option value="Databases">Databases</option>
                     <option value="Tools">Tools</option>
                     <option value="Cloud Platforms">Cloud Platforms</option>
                     <option value="Manual Testing">Manual Testing</option>
                     <option value="Automation Testing">Automation Testing</option>
                     <option value="API Tools">API Tools</option>
                     <option value="Version Control/CI/Build Tool">Version Control/CI/Build Tool</option>
                     <option value="Web Automation">Web Automation</option>
                     <option value="Defect Tracking Tools">Defect Tracking Tools</option>
                     <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    value={skillForm.description}
                    onChange={(e) => setSkillForm({...skillForm, description: e.target.value})}
                    className="input-field"
                    rows="3"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowSkillModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingSkill ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Project Modal */}
      {showProjectModal && (
        <div className="modal-overlay">
          <div className="modal-content large" style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h3>{editingProject ? 'Edit Project' : 'Add New Project'}</h3>
              <button
                onClick={() => {
                  setShowProjectModal(false);
                  console.log('Closing project modal, showProjectModal:', false);
                }}
                className="modal-close"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleProjectSubmit} className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Project Name *</label>
                  <input
                    type="text"
                    value={projectForm.name}
                    onChange={(e) => setProjectForm({...projectForm, name: e.target.value})}
                    required
                    className="input-field"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select
                    value={projectForm.category}
                    onChange={(e) => setProjectForm({...projectForm, category: e.target.value})}
                    required
                    className="input-field"
                  >
                    <option value="">Select Category</option>
                    <option value="Web Development">Web Development</option>
                    <option value="Mobile App">Mobile App</option>
                    <option value="Data Science">Data Science</option>
                    <option value="System">System</option>
                    <option value="API Development">API Development</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Difficulty *</label>
                  <select
                    value={projectForm.difficulty}
                    onChange={(e) => setProjectForm({...projectForm, difficulty: e.target.value})}
                    required
                    className="input-field"
                  >
                    <option value="">Select Difficulty</option>
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Technologies *</label>
                  <input
                    type="text"
                    value={projectForm.technologies}
                    onChange={(e) => setProjectForm({...projectForm, technologies: e.target.value})}
                    required
                    className="input-field"
                    placeholder="e.g., React, Node.js, MongoDB"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Project Client</label>
                  <input
                    type="text"
                    value={projectForm.client}
                    onChange={(e) => setProjectForm({...projectForm, client: e.target.value})}
                    className="input-field"
                    placeholder="Enter client name (if any)"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Project Domain</label>
                  <input
                    type="text"
                    value={projectForm.domain}
                    onChange={(e) => setProjectForm({...projectForm, domain: e.target.value})}
                    className="input-field"
                    placeholder="Enter project domain (e.g., Finance, Healthcare)"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Project Description *</label>
                <ReactQuill
                  value={projectForm.description}
                  onChange={value => setProjectForm({...projectForm, description: value})}
                  required
                  className="input-field quill-field"
                  rows="10"
                  placeholder="Describe the project, its features, and what it accomplishes..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">Responsibilities *</label>
                <ReactQuill
                  value={projectForm.responsibilities}
                  onChange={value => setProjectForm({...projectForm, responsibilities: value})}
                  required
                  className="input-field quill-field"
                  rows="10"
                  placeholder="List the key responsibilities and tasks you performed..."
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowProjectModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingProject ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    <SuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} message={successMessage} />
    <DeleteConfirmModal
      isOpen={showDeleteModal}
      onClose={() => setShowDeleteModal(false)}
      onConfirm={() => {
        if (deleteType === 'skill') handleDeleteSkill(itemToDelete);
        if (deleteType === 'project') handleDeleteProject(itemToDelete);
        setItemToDelete(null);
        setDeleteType('');
      }}
      message={`Are you sure you want to delete this ${deleteType}? This action cannot be undone.`}
      title={`Delete ${deleteType.charAt(0).toUpperCase() + deleteType.slice(1)}`}
    />
    </>
  );
};

export default AdminMasterData; 